import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, ActivatedRoute } from '@angular/router';
import { map, concatMap } from 'rxjs/operators';
import { OrigamiUrlsParams } from '@app/origami-api/constants';
import { LoginToRoomService, AuthService } from '@app/services';
import { SessionService } from '@app/session/session.service';
import { of } from 'rxjs';
import { AppRoutes } from '@app/app-routes';
import { FolderManagementService } from '@app/origami-api/services/folder-management.service';
import { RoomTypes } from '@app/origami-api/constants/room-types';
import { ServiceOfferingTypes } from '@app/shared/forms/dynamic-form/dynamic-fields/custom-fields/room-fields/room-form-general-room-options/room-form-general-room-options.component';
import { IgnoreCTMSUIRequestParameter } from '@app/ti-components/documents/document-viewer/document-viewer.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentViewerModuleGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private sessionService: SessionService,
    private loginToRoomService: LoginToRoomService,
    private folderManagementService: FolderManagementService,
    private activatedRoute: ActivatedRoute,
  ) {}

  canActivate = (route: ActivatedRouteSnapshot) => {
    return this.authService.checkSession().pipe(
      concatMap((auth) => {
        if (auth.hasSession && !auth.registrationMode) {
          if (auth.info.HasAccessToAnyRoom) {
            const roomId = +route.paramMap.get('roomId');
            const documentId = +route.paramMap.get('documentId');

            return this.sessionService.enterDomain().pipe(
              concatMap(() => this.loginToRoomService.loginWithConfidentialityAgreementId(roomId)),
              concatMap((loginRes) => {
                if (!loginRes.error && loginRes.details) {
                  return this.sessionService
                    .enterRoom(roomId)
                    .pipe(map(() => ({ loggedIn: true, hasAccessToAnyRoom: true, enteredRoom: true })));
                }
                return of({ loggedIn: true, hasAccessToAnyRoom: true, enteredRoom: false });
              }),
              concatMap((roomResult) => {
                const queryParams = {};
                const isRedirectable =
                  this.sessionService?.room?.info?.RoomTypeId === RoomTypes.Collaborate &&
                  this.sessionService?.room.info?.ServiceOffering === ServiceOfferingTypes.CCR;
                if (isRedirectable) {
                  const url = window.location.href;
                  //soff=0 already sent, we can remove it, to do: remove all server side params
                  const theURL = new URL(url.replace('?soff=0', '').replace('#/', ''));
                  if (theURL) {
                    theURL.searchParams?.forEach((value, key) => {
                      queryParams[key] = value;
                    });
                  }
                }
                return !(isRedirectable && queryParams[IgnoreCTMSUIRequestParameter] !== '1')
                  ? of(roomResult)
                  : this.folderManagementService
                      .getWorkingDocumentId({
                        documentId,
                        siteId: roomId,
                      })
                      .pipe(
                        map((wdoc) => {
                          const workingDocumentId = +wdoc?.Result;
                          if (wdoc.Success && workingDocumentId && documentId !== workingDocumentId) {
                            this.router.navigate([`/document-viewer/${roomId}/${workingDocumentId}`], {
                              queryParams: queryParams,
                              queryParamsHandling: 'merge',
                            });
                          } else {
                            return roomResult;
                          }
                        }),
                      );
              }),
            );
          }
          return of({ loggedIn: true, hasAccessToAnyRoom: false, enteredRoom: false });
        }
        return of({ loggedIn: false, hasAccessToAnyRoom: false, enteredRoom: false });
      }),
      map((res) => {
        this.goToAlterUrl({
          loggedIn: res.loggedIn,
          hasAccessToAnyRoom: res.hasAccessToAnyRoom,
          enteredRoom: res.enteredRoom,
          url: route.url.join('/'),
        });

        return res.loggedIn && res.hasAccessToAnyRoom && res.enteredRoom;
      }),
    );
  };

  canDeactivate = () =>
    this.sessionService.leaveRoom().pipe(
      concatMap(() => this.sessionService.leaveDomain()),
      map(() => true),
    );

  canLoad = () =>
    this.authService.checkSession().pipe(
      map((auth) => {
        const loggedIn = auth.hasSession && !auth.registrationMode;

        this.goToAlterUrl({
          loggedIn: loggedIn,
          hasAccessToAnyRoom: loggedIn && auth.info.HasAccessToAnyRoom,
        });

        return loggedIn;
      }),
    );

  private goToAlterUrl(res: { loggedIn: boolean; hasAccessToAnyRoom: boolean; enteredRoom?: boolean; url?: string }) {
    if (!res.loggedIn) {
      const queryParams = { [OrigamiUrlsParams.returnRoute]: escape(res.url) };
      this.router.navigate(['/auth'], { queryParams: queryParams });
    } else if (!res.hasAccessToAnyRoom) {
      this.router.navigate(['/no-access']);
    } else if (res.enteredRoom === false) {
      this.router.navigate([AppRoutes.Main]);
    }
  }
}
